import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import axios from '../../../services/axios';
import Modal from '../../../components/Modal/Modal';
import EmployeeNameSearch from '../../../components/EmployeeNameSearch/';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';

// =============================================================================
// Estado inicial do formulário
// =============================================================================

const EMPTY_FORM_STATE = {
   request_date:              '',
   description:               '',
   principal_amount:          '',
   interest_rate_percentage:  '0',
   total_installments_count:  '1',
   first_reference_month:     '',
   payment_method_used:       '',
   notes:                     '',
};

// =============================================================================
// Componente
// =============================================================================

/**
 * Modal para criação de adiantamento salarial desacoplado do employeeId.
 * O funcionário é selecionado via autocomplete dentro do próprio modal.
 *
 * Props:
 *   isOpen    {boolean}  — controla visibilidade
 *   onClose   {fn}       — chamado ao fechar
 *   onSuccess {fn}       — chamado após criação bem-sucedida (para refetch da listagem)
 */
export default function NewSalaryAdvanceModal({ isOpen, onClose, onSuccess }) {
   const [selectedEmployee, setSelectedEmployee]   = useState(null);
   const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
   const [formData, setFormData]                   = useState(EMPTY_FORM_STATE);
   const [isSubmitting, setIsSubmitting]           = useState(false);

   // ─── Fechar e limpar estado ────────────────────────────────────
   const handleClose = useCallback(() => {
      setSelectedEmployee(null);
      setEmployeeSearchQuery('');
      setFormData(EMPTY_FORM_STATE);
      onClose();
   }, [onClose]);

   // ─── Handlers de campo ────────────────────────────────────────
   const handleFieldChange = (event) => {
      const { name, value } = event.target;
      setFormData((previous) => ({ ...previous, [name]: value }));
   };

   // ─── Submit ───────────────────────────────────────────────────
   const handleSubmit = async () => {
      if (!selectedEmployee) {
         toast.error('Selecione um funcionário.');
         return;
      }
      if (!formData.request_date) {
         toast.error('Data da solicitação é obrigatória.');
         return;
      }
      if (!formData.description.trim()) {
         toast.error('Descrição é obrigatória.');
         return;
      }
      if (!formData.principal_amount || Number(formData.principal_amount) <= 0) {
         toast.error('Valor principal deve ser maior que zero.');
         return;
      }
      if (!formData.total_installments_count || Number(formData.total_installments_count) < 1) {
         toast.error('Número de parcelas deve ser ao menos 1.');
         return;
      }
      if (!formData.first_reference_month) {
         toast.error('Mês de referência da 1ª parcela é obrigatório.');
         return;
      }

      setIsSubmitting(true);
      try {
         await axios.post(
            `/hr/employees/${selectedEmployee.id}/salary-advances`,
            {
               ...formData,
               principal_amount:         Number(formData.principal_amount),
               interest_rate_percentage: Number(formData.interest_rate_percentage || 0),
               total_installments_count: Number(formData.total_installments_count),
               payment_method_used:      formData.payment_method_used || null,
               notes:                    formData.notes || null,
            },
         );

         toast.success(`Adiantamento registrado para ${selectedEmployee.full_name}!`);
         handleClose();
         onSuccess?.();
      } catch (error) {
         const errorMessage =
            error?.response?.data?.errors?.[0] ??
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao registrar adiantamento.';
         toast.error(errorMessage);
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         title="Novo adiantamento salarial"
         onClose={handleClose}
         maxWidth="600px"
      >
         <FormGrid $cols={12} $colsMobile={12}>

            {/* ─── Seleção de funcionário ──────────────────────── */}
            <Field $col={12} $colMobile={12}>
               <label htmlFor="advance-employee">Funcionário *</label>
               <EmployeeNameSearch
                  value={employeeSearchQuery}
                  onChange={setEmployeeSearchQuery}
                  onSelect={(employee) => {
                     setSelectedEmployee(employee);
                     setEmployeeSearchQuery(employee?.full_name ?? '');
                  }}
                  required
                  placeholder="Digite o nome do funcionário..."
                  showHint
               />
               {selectedEmployee && (
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                     {selectedEmployee.jobPosition?.position_name ?? ''}
                     {selectedEmployee.department?.department_name
                        ? ` · ${selectedEmployee.department.department_name}`
                        : ''}
                  </p>
               )}
            </Field>

            {/* ─── Campos do adiantamento ──────────────────────── */}
            <Field $col={6} $colMobile={12}>
               <label htmlFor="advance-request-date">Data da solicitação *</label>
               <input
                  id="advance-request-date"
                  type="date"
                  name="request_date"
                  value={formData.request_date}
                  onChange={handleFieldChange}
               />
            </Field>

            <Field $col={6} $colMobile={12}>
               <label htmlFor="advance-description">Descrição *</label>
               <input
                  id="advance-description"
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleFieldChange}
                  placeholder="Ex: Adiantamento salarial, Compra moto BH..."
               />
            </Field>

            <Field $col={4} $colMobile={6}>
               <label htmlFor="advance-principal-amount">Valor principal (R$) *</label>
               <input
                  id="advance-principal-amount"
                  type="number"
                  name="principal_amount"
                  value={formData.principal_amount}
                  onChange={handleFieldChange}
                  step="0.01"
                  min="0.01"
                  placeholder="0,00"
               />
            </Field>

            <Field $col={4} $colMobile={6}>
               <label htmlFor="advance-interest-rate">Juros (%)</label>
               <input
                  id="advance-interest-rate"
                  type="number"
                  name="interest_rate_percentage"
                  value={formData.interest_rate_percentage}
                  onChange={handleFieldChange}
                  step="0.01"
                  min="0"
                  placeholder="0"
               />
            </Field>

            <Field $col={4} $colMobile={6}>
               <label htmlFor="advance-installments-count">Nº de parcelas *</label>
               <input
                  id="advance-installments-count"
                  type="number"
                  name="total_installments_count"
                  value={formData.total_installments_count}
                  onChange={handleFieldChange}
                  min="1"
                  step="1"
               />
            </Field>

            <Field $col={6} $colMobile={6}>
               <label htmlFor="advance-first-reference-month">1º mês de desconto *</label>
               <input
                  id="advance-first-reference-month"
                  type="month"
                  name="first_reference_month"
                  value={formData.first_reference_month}
                  onChange={handleFieldChange}
               />
            </Field>

            <Field $col={6} $colMobile={12}>
               <label htmlFor="advance-payment-method">Forma de pagamento</label>
               <input
                  id="advance-payment-method"
                  type="text"
                  name="payment_method_used"
                  value={formData.payment_method_used}
                  onChange={handleFieldChange}
                  placeholder="Ex: PIX Caixa, Boleto..."
               />
            </Field>

            <Field $col={12} $colMobile={12}>
               <label htmlFor="advance-notes">Observações</label>
               <input
                  id="advance-notes"
                  type="text"
                  name="notes"
                  value={formData.notes}
                  onChange={handleFieldChange}
                  placeholder="Observações opcionais..."
               />
            </Field>

            {/* ─── Ações ───────────────────────────────────────── */}
            <Field $col={12} $colMobile={12}>
               <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '4px' }}>
                  <button type="button" onClick={handleClose} disabled={isSubmitting}>
                     Cancelar
                  </button>
                  <button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                     {isSubmitting ? 'Registrando...' : 'Registrar adiantamento'}
                  </button>
               </div>
            </Field>

         </FormGrid>
      </Modal>
   );
}

NewSalaryAdvanceModal.propTypes = {
   isOpen:    PropTypes.bool.isRequired,
   onClose:   PropTypes.func.isRequired,
   onSuccess: PropTypes.func,
};
